import React, { FC, useCallback, useRef } from "react";
import { SearchWidgetConfig, SearchWidgetTheme } from "../../config";
import { log } from "../../utils";
import { SearchStoreProvider, useSearchDispatch, useSearchState } from "../../store";
import { ModalDialog } from "../ModalDialog";
import { SearchInputContainer } from "../SearchInput";
import { SearchWidget } from "../SearchWidget";
import { WidgetStylesheet } from "../WidgetStylesheet";
import { classNames } from "../../constants/variables";
import "./Search.scss";
import { ChatButton } from "../ChatButton";

export interface SearchProps {
    readonly config: SearchWidgetConfig;
    readonly theme?: SearchWidgetTheme;
    /**
     * Optional elements that will act as the button to enable the modal search.
     */
    readonly button?: JSX.Element | string;
    readonly buttonElementLookup?: () => Promise<HTMLElement>;
}

function noop() {}

export const SearchContainer: FC<SearchProps> = (props) => {
    return (
        <SearchStoreProvider>
            <Search
                config={props.config}
                theme={props.theme}
                button={props.button}
                buttonElementLookup={props.buttonElementLookup}
            />
        </SearchStoreProvider>
    );
};

export const Search: FC<SearchProps> = (props) => {
    const { config, theme } = props;

    const buttonId = useRef("");

    const dispatch = useSearchDispatch();

    const { active, minimized } = useSearchState();

    const handleFocus = useCallback(() => {
        log("Toggle xapp widget was called");

        if (minimized || active) {
            dispatch({
                type: "new-search",
            });

            dispatch({
                type: "set-modal-state",
                active: false,
                minimized: false,
                autoGreeting: false,
            });
        } else {
            dispatch({
                type: "set-modal-state",
                active: true,
                minimized: false,
                autoGreeting: true,
            });
        }
    }, [active, dispatch, minimized]);

    const handleButtonClick = useCallback(() => {
        dispatch({
            type: "set-modal-state",
            active: true,
            minimized: false,
            autoGreeting: false,
        });
    }, [dispatch]);

    const handleClose = useCallback(() => {
        dispatch({
            type: "set-modal-state",
            active: false,
            minimized: false,
            autoGreeting: false,
        });
    }, [dispatch]);

    // For external focus calls (window._xapp.toggleWidget())
    window["_xapp"] = {
        toggleWidget: () => {
            log("External focus call");
            handleFocus();
        },
    };

    let el: HTMLElement | null;

    if (props.buttonElementLookup) {
        props.buttonElementLookup().then((buttonElement: HTMLElement) => {
            if (buttonElement) {
                buttonElement.onclick = handleFocus;
                buttonElement.onfocus = handleFocus;

                if (buttonId.current !== buttonElement.id) {
                    log(`xapp search button was updated. ID: ${buttonElement.id}`);
                }

                buttonId.current = buttonElement.id;
            }
        });
    } else {
        const elements = document.getElementsByClassName(
            classNames.XAPP_SEARCH_BUTTON
        ) as HTMLCollectionOf<HTMLElement>;

        if (elements.length) {
            el = elements[0];
            log(`xapp search button was found (first by class): ${classNames.XAPP_SEARCH_BUTTON}`);
        } else {
            el = document.getElementById(classNames.XAPP_SEARCH_BUTTON);
            if (el) {
                log(`xapp search button was found (by id): ${classNames.XAPP_SEARCH_BUTTON}`);
            }
        }

        if (el) {
            el.onclick = handleFocus;
            el.onfocus = handleFocus;
        } else {
            console.warn(`xapp search button element was not found`);
        }
    }

    let button: JSX.Element;

    if (props.button) {
        if (typeof props.button === "object") {
            // If they provided a button, we add the onClick
            button = (
                <props.button.type
                    {...props.button.props}
                    onClick={handleFocus}
                    onFocus={handleFocus}
                />
            );
        } else {
            // We are using an ID for the button, just put in a placeholder
            button = <></>;
        }
    } else {
        // no button prop provided so use default search container
        button = (
            <SearchInputContainer
                onPrev={noop}
                onNext={noop}
                onSubmit={noop}
                onFocus={handleFocus}
                audioOn={false}
                loading={false}
                outputSpeech={""}
            />
        );
    }

    return (
        <div className="xapp-search">
            <WidgetStylesheet theme={theme} />

            {button}

            {minimized && <ChatButton onClick={handleButtonClick} />}

            {active && (
                <ModalDialog onClose={handleClose}>
                    <SearchWidget config={config} />
                </ModalDialog>
            )}
        </div>
    );
};
